import { Button, Input } from '@/components/ui'
import { AlertCircle, ArrowLeft, ArrowRight, Building2, CheckCircle2, LockKeyhole, Mail } from 'lucide-react'

import type { AxiosError } from 'axios'
import { completeLogin, getLoginOptions } from '@/api/services/auth.service'
import { getMyCompanies } from '@/api/services/companies.service'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/useAuthStore'
import { queryClient } from '@/main'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import type { LoginOrganizationOption } from '@/types/api.types'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { getTenantSlugFromHost, isTenantSubdomainsEnabled, tenantUrl } from '@/lib/tenantHost'

const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  password: z.string().min(1, 'Password is required').min(6, 'Password must be at least 6 characters'),
})

type LoginFormValues = z.infer<typeof loginSchema>

interface OrganizationChoiceState {
  selectionToken: string
  organizations: LoginOrganizationOption[]
}

interface LoginFormProps {
  /** Set on a tenant subdomain: sign in to this organization only, no picker. */
  organizationSlug?: string
}

/**
 * Login form with validation and auth mutation.
 */
export function LoginForm({ organizationSlug }: LoginFormProps = {}): React.ReactElement {
  const navigate = useNavigate()
  const setAuth = useAuthStore((s) => s.setAuth)
  const [apiError, setApiError] = useState<string | null>(null)
  const [organizationChoice, setOrganizationChoice] =
    useState<OrganizationChoiceState | null>(null)
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const finishLogin = async (user: Parameters<typeof setAuth>[0]): Promise<void> => {
    toast.success('Signed in')
    queryClient.clear()
    setAuth(user)
    let target = '/dashboard'
    try {
      const companies = await getMyCompanies()
      if (companies.length > 1) {
        target = '/companies'
      } else if (companies.length === 1) {
        target = `/companies/${companies[0]._id}`
      }
    } catch {
      // Fall through to the dashboard.
    }
    // Signed in from the main site: continue on the organization's own
    // subdomain. The session cookie lives on the API host, so it carries over.
    const tenantSlug = user.organizationSlug
    if (
      isTenantSubdomainsEnabled() &&
      tenantSlug &&
      getTenantSlugFromHost() !== tenantSlug
    ) {
      window.location.replace(tenantUrl(tenantSlug, target))
      return
    }
    navigate(target, { replace: true })
  }

  const completeMutation = useMutation({
    mutationFn: completeLogin,
    onSuccess: async (data) => {
      await finishLogin(data.user)
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const message = error.response?.data?.message ?? 'Login failed. Please try again.'
      setApiError(message)
    },
  })

  const optionsMutation = useMutation({
    mutationFn: getLoginOptions,
    onSuccess: (data) => {
      if (data.organizations.length === 1) {
        const organization = data.organizations[0]
        setSelectedSlug(organization.slug)
        completeMutation.mutate({
          selectionToken: data.selectionToken,
          organizationSlug: organization.slug,
        })
        return
      }

      setSelectedSlug(null)
      setOrganizationChoice({
        selectionToken: data.selectionToken,
        organizations: data.organizations,
      })
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const message = error.response?.data?.message ?? 'Login failed. Please try again.'
      setApiError(message)
    },
  })

  const onSubmit = (values: LoginFormValues): void => {
    setApiError(null)
    setOrganizationChoice(null)
    optionsMutation.mutate({
      email: values.email,
      password: values.password,
      organizationSlug,
    })
  }

  const handleBack = (): void => {
    setOrganizationChoice(null)
    setSelectedSlug(null)
    setApiError(null)
    completeMutation.reset()
    optionsMutation.reset()
  }

  const handleChooseOrganization = (organizationSlug: string): void => {
    if (!organizationChoice) return
    setApiError(null)
    setSelectedSlug(organizationSlug)
    completeMutation.mutate({
      selectionToken: organizationChoice.selectionToken,
      organizationSlug,
    })
  }

  const isSubmittingCredentials = optionsMutation.isPending
  const isCompletingLogin = completeMutation.isPending

  return (
    <form
        method="post"
        action="."
        onSubmit={(e) => {
          e.preventDefault()
          void handleSubmit(onSubmit)(e)
        }}
        className="space-y-5"
      >
        {apiError && (
          <div
            role="alert"
            aria-live="polite"
            className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-800"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span className="leading-5">{apiError}</span>
          </div>
        )}
        {organizationChoice ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-gray-900">Choose an organization</p>
                <p className="mt-1 text-xs text-gray-500">Select which one to use today.</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="shrink-0"
                onClick={handleBack}
                disabled={isCompletingLogin}
              >
                <ArrowLeft className="mr-1.5 h-4 w-4" aria-hidden />
                Back
              </Button>
            </div>
            <div className="space-y-2">
              {organizationChoice.organizations.map((organization) => {
                const isSelected = selectedSlug === organization.slug
                const color = organization.branding?.primaryColor ?? '#0f5b78'
                return (
                  <button
                    key={organization.id}
                    type="button"
                    onClick={() => handleChooseOrganization(organization.slug)}
                    disabled={isCompletingLogin}
                    className="flex w-full items-center gap-3 rounded-lg border border-gray-200 bg-white px-3.5 py-3 text-left transition hover:border-primary/40 hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {organization.branding?.logo ? (
                      <img
                        src={organization.branding.logo}
                        alt=""
                        className="h-10 w-10 rounded-lg border border-gray-200 object-contain"
                      />
                    ) : (
                      <span
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-white"
                        style={{ backgroundColor: color }}
                        aria-hidden
                      >
                        <Building2 className="h-5 w-5" />
                      </span>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-gray-900">
                        {organization.name}
                      </span>
                      <span className="block truncate text-xs text-gray-500">
                        {organization.slug}
                      </span>
                    </span>
                    {isSelected && isCompletingLogin ? (
                      <span className="text-xs font-medium text-primary">Signing in…</span>
                    ) : isSelected ? (
                      <CheckCircle2 className="h-5 w-5 text-primary" aria-hidden />
                    ) : null}
                  </button>
                )
              })}
            </div>
          </div>
        ) : (
          <>
            <Input
              label="Email"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              leadingIcon={<Mail className="h-4 w-4" />}
              error={errors.email?.message}
              className="h-12 text-base caret-primary md:text-sm dark:border-gray-300 dark:bg-white dark:text-gray-900 dark:placeholder:text-gray-500"
              {...register('email')}
            />
            <Input
              label="Password"
              type="password"
              autoComplete="current-password"
              placeholder="Enter your password"
              leadingIcon={<LockKeyhole className="h-4 w-4" />}
              showPasswordToggle
              error={errors.password?.message}
              className="h-12 text-base caret-primary md:text-sm dark:border-gray-300 dark:bg-white dark:text-gray-900 dark:placeholder:text-gray-500"
              {...register('password')}
            />
            <div className="-mt-1 flex justify-end">
              <Link
                to="/forgot-password"
                className="rounded-sm text-sm font-medium text-[var(--public-muted)] underline-offset-4 transition-colors hover:text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
              >
                Forgot password?
              </Link>
            </div>
            <Button
              type="submit"
              size="lg"
              className="group h-12 w-full touch-manipulation rounded-xl text-[0.9375rem] font-semibold"
              loading={isSubmittingCredentials || isCompletingLogin}
              loadingLabel="Signing in"
            >
              {isSubmittingCredentials || isCompletingLogin ? (
                'Signing in…'
              ) : (
                <>
                  Sign in
                  <ArrowRight
                    className="ml-0.5 h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5"
                    aria-hidden
                  />
                </>
              )}
            </Button>
          </>
        )}
      </form>
  )
}
